import { useTu } from "@src/appV2/i18n/useTu";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { alpha, Box, Stack, type SvgIconProps } from "@mui/material";
import { type ComponentType } from "react";
import { Text } from "../../components/Text";
import { applyPlatformAwareInteractionStyles } from "../../utils/applyPlatformAwareInteractionStyles";
import { ALL_SUPPORT_ARTICLES_CATEGORY } from "../constants";
import { getMutedText, getStrongText } from "../themeHelpers";

const CATEGORY_ICON_MAP: Partial<Record<string, ComponentType<SvgIconProps>>> = {
  "Account Management": SettingsOutlinedIcon,
  "Cancellations/NS/Late Arrivals": CalendarTodayOutlinedIcon,
  "Shift Booking": DateRangeOutlinedIcon,
  "Worker Complaints/Safety Issues": ReportProblemOutlinedIcon,
  "Worker Management": PeopleOutlinedIcon,
};

const DefaultCategoryIcon = DescriptionOutlinedIcon;

interface HelpCenterKnowledgeBaseProps {
  articleCountByCategory: Map<string, number>;
  displayCategories: string[];
  isMobile: boolean;
  selectedCategory: string;
  onSelectCategory: (selectedCategory: string) => void;
}

export function HelpCenterKnowledgeBase(props: HelpCenterKnowledgeBaseProps) {
  const { tu } = useTu("helpCenter");

  const {
    articleCountByCategory,
    displayCategories,
    isMobile,
    selectedCategory,
    onSelectCategory,
  } = props;

  if (displayCategories.length === 0) {
    return null;
  }

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Text
          bold
          variant="body1"
          sx={(theme) => ({
            fontFamily: theme.typography.body1.fontFamily,
            fontSize: "1.5rem",
            lineHeight: 1.2,
            color: getStrongText(theme),
          })}
        >
          {tu("Help by topic")}
        </Text>
        <Text
          variant="body2"
          sx={(theme) => ({ color: getMutedText(theme), fontSize: "0.8125rem" })}
        >
          {tu("Choose a topic below to find answers and instructions.")}
        </Text>
      </Stack>
      <Box
        sx={(theme) => ({
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
          gap: theme.spacing(4),
        })}
      >
        {displayCategories.map((category) => {
          const isSelected =
            category === selectedCategory && selectedCategory !== ALL_SUPPORT_ARTICLES_CATEGORY;
          const count = articleCountByCategory.get(category) ?? 0;
          const CategoryIcon = CATEGORY_ICON_MAP[category] ?? DefaultCategoryIcon;

          return (
            <Box
              key={category}
              aria-label={category}
              component="button"
              type="button"
              sx={(theme) => ({
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: theme.spacing(1),
                width: "100%",
                paddingTop: theme.spacing(3),
                paddingRight: theme.spacing(3),
                paddingBottom: theme.spacing(3),
                paddingLeft: theme.spacing(3),
                borderRadius: theme.borderRadius?.large,
                border: "2px solid transparent",
                backgroundColor: isSelected
                  ? (theme.palette.base?.base ?? theme.palette.text.primary)
                  : theme.palette.background.paper,
                boxShadow: theme.shadow?.card,
                cursor: "pointer",
                textAlign: "left",
                transition: theme.transitions.create(
                  ["background-color", "transform", "box-shadow"],
                  { duration: theme.transitions.duration.shorter }
                ),
                ...applyPlatformAwareInteractionStyles({
                  hoverStyle: {
                    transform: "translateY(-1px)",
                    boxShadow: `0 0.5rem 1.25rem ${alpha(getStrongText(theme), 0.08)}`,
                  },
                }),
              })}
              onClick={() => {
                onSelectCategory(
                  category === selectedCategory ? ALL_SUPPORT_ARTICLES_CATEGORY : category
                );
              }}
            >
              <CategoryIcon
                sx={(theme) => ({
                  fontSize: "1.125rem",
                  color: isSelected
                    ? (theme.palette.base?.inverted?.strong ?? "#FFFFFF")
                    : getStrongText(theme),
                })}
              />
              <Text
                semibold
                variant="body2"
                sx={(theme) => ({
                  fontFamily: theme.typography.body1.fontFamily,
                  fontSize: "0.875rem",
                  color: isSelected
                    ? (theme.palette.base?.inverted?.strong ?? "#FFFFFF")
                    : theme.palette.text.primary,
                })}
              >
                {category}
              </Text>
              {count > 0 && (
                <Text
                  variant="caption"
                  sx={(theme) => ({
                    color: isSelected
                      ? (theme.palette.base?.inverted?.muted ?? "rgba(255,255,255,0.5)")
                      : getMutedText(theme),
                  })}
                >
                  {count} {count === 1 ? "article" : "articles"}
                </Text>
              )}
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}
